import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import {
  signInWithEmailAndPassword, signOut, onIdTokenChanged, setPersistence,
  browserSessionPersistence, type User,
} from 'firebase/auth'
import { httpsCallable } from 'firebase/functions'
import { auth, functions } from '@/firebase'
import { isAdminClaim, isMfaValid, mfaRemainingMs, type TokenClaims } from '@/lib/claims'
import { isValidTotp, normaliseTotp } from '@/lib/validate'
import { log } from '@/lib/logger'

export type AuthStage = 'loading' | 'signedOut' | 'needsMfa' | 'needsEnrolment' | 'ready'

export interface EnrolmentPayload {
  otpauthUri: string
  qrDataUrl: string
  recovery: string[]
}

/**
 * Two-step admin sign-in.
 *
 *   password  →  signedIn but claims.mfa is false  →  admin screens stay shut
 *   TOTP code →  Cloud Function sets the claim     →  token refreshed, screens open
 *
 * The client never decides whether MFA passed. It asks the function, and the
 * function is the only thing that can set the claim the Firestore rules read.
 */
export const useAuthStore = defineStore('auth', () => {
  const user = ref<User | null>(null)
  const claims = ref<TokenClaims | null>(null)
  const stage = ref<AuthStage>('loading')
  const error = ref<string | null>(null)
  const busy = ref(false)

  const isAdmin = computed(() => isAdminClaim(claims.value))
  const mfaOk = computed(() => isMfaValid(claims.value))
  const canAdminister = computed(() => isAdmin.value && mfaOk.value)
  const mfaRemaining = computed(() => mfaRemainingMs(claims.value))

  function deriveStage(): void {
    if (!user.value) { stage.value = 'signedOut'; return }
    if (!isAdmin.value) { stage.value = 'signedOut'; return }
    stage.value = mfaOk.value ? 'ready' : 'needsMfa'
  }

  /** Session persistence, not local: closing the browser drops the session. */
  async function init(): Promise<void> {
    await setPersistence(auth, browserSessionPersistence)
    onIdTokenChanged(auth, async (u) => {
      user.value = u
      if (u) {
        const token = await u.getIdTokenResult()
        claims.value = token.claims as TokenClaims
        log.info('auth state', { uid: u.uid, admin: isAdmin.value, mfa: mfaOk.value })
      } else {
        claims.value = null
      }
      deriveStage()
    })
  }

  async function signIn(email: string, password: string): Promise<void> {
    error.value = null
    busy.value = true
    try {
      const cred = await signInWithEmailAndPassword(auth, email, password)
      const token = await cred.user.getIdTokenResult(true)
      // Set both here rather than waiting for onIdTokenChanged. The listener
      // fires a tick later, and until it does the UI would read as signed out.
      user.value = cred.user
      claims.value = token.claims as TokenClaims
      if (!isAdminClaim(claims.value)) {
        await signOut(auth)
        user.value = null
        claims.value = null
        error.value = 'That account is not an administrator.'
        log.warn('non-admin sign-in refused', { uid: cred.user.uid })
        return
      }
      deriveStage()
    } catch (err) {
      // Deliberately vague: distinguishing "no such user" from "wrong password"
      // tells an attacker which addresses are real.
      error.value = 'Email or password is not right.'
      log.warn('sign-in failed', { err })
    } finally {
      busy.value = false
    }
  }

  async function submitMfa(code: string): Promise<boolean> {
    error.value = null
    if (!isValidTotp(code)) {
      error.value = 'Enter the six digits from your authenticator app.'
      return false
    }
    busy.value = true
    try {
      const verify = httpsCallable<{ code: string }, { ok: boolean }>(functions, 'verifyMfa')
      await verify({ code: normaliseTotp(code) })
      // Force a refresh so the new claim is in the token the rules will read.
      const current = auth.currentUser
      if (!current) { error.value = 'Session expired. Sign in again.'; return false }
      const token = await current.getIdTokenResult(true)
      user.value = current
      claims.value = token.claims as TokenClaims
      deriveStage()
      log.info('mfa accepted')
      return mfaOk.value
    } catch (err) {
      const code = (err as { code?: string })?.code ?? ''
      error.value = code.includes('resource-exhausted')
        ? 'Too many wrong codes. Wait a few minutes and try again.'
        : code.includes('failed-precondition')
          ? 'This account has no authenticator set up yet.'
          : 'That code is not right.'
      log.warn('mfa rejected', { err })
      return false
    } finally {
      busy.value = false
    }
  }

  async function startEnrolment(): Promise<EnrolmentPayload | null> {
    busy.value = true
    try {
      const fn = httpsCallable<void, EnrolmentPayload>(functions, 'startMfaEnrolment')
      return (await fn()).data
    } catch (err) {
      error.value = 'Could not start enrolment.'
      log.error('enrolment start failed', { err })
      return null
    } finally {
      busy.value = false
    }
  }

  async function confirmEnrolment(code: string): Promise<boolean> {
    if (!isValidTotp(code)) { error.value = 'Enter the six digits shown in the app.'; return false }
    busy.value = true
    try {
      const fn = httpsCallable<{ code: string }, { ok: boolean }>(functions, 'confirmMfaEnrolment')
      await fn({ code: normaliseTotp(code) })
      return true
    } catch {
      error.value = 'That code did not match. Wait for the next one and try again.'
      return false
    } finally {
      busy.value = false
    }
  }

  async function logOut(): Promise<void> {
    try {
      await httpsCallable(functions, 'revokeMfa')()
    } catch (err) {
      log.warn('revoke on sign out failed', { err })
    }
    await signOut(auth)
    user.value = null
    claims.value = null
    stage.value = 'signedOut'
    log.info('signed out')
  }

  return {
    user, claims, stage, error, busy,
    isAdmin, mfaOk, canAdminister, mfaRemaining,
    init, signIn, submitMfa, startEnrolment, confirmEnrolment, logOut,
  }
})
